// WALLET-P1 §6.2: a new price version, a modal — the platform list's (`operator.tariffs.create`)
// or one company's custom prices (`operator.wallet.createTariff`). Versions are append-only, so
// "Edit" opens this prefilled from the row and saves a new version.
import { KNOWN_TARIFF_UNITS } from "@companyos/schema/contracts";
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  InfoTip,
  Input,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import { bpText, parsePercent, parseRate, rateToInput } from "../../billing/billing-money";
import { problemText } from "../../ops-parts";
import { type PlanPrice, PlanPricesEditor } from "./plan-prices";
import {
  type CategoryMarkup,
  CategoryMarkupsEditor,
  type UnitPrice,
  UnitPricesEditor,
} from "./unit-lines";

export type TariffRates = {
  label: string;
  effective_from: string;
  input_per_m_micros: number;
  output_per_m_micros: number;
  image_micros: number;
  unit_prices: UnitPrice[];
  reported_cost_markup_bp: number;
  category_markups: CategoryMarkup[];
  /** OPERATOR-CONSOLE: per-plan prices (the platform list only). */
  plan_prices?: PlanPrice[];
};

const tomorrow = () => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);

export function TariffDialog({
  api,
  open,
  onOpenChange,
  title,
  from,
  companyId,
  onSaved,
}: {
  api: Api;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Prefill (Edit = a new version from this one). */
  from?: TariffRates | null;
  /** A company's custom prices; the platform list when absent. */
  companyId?: string;
  onSaved: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-slot="tariff-dialog"
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
      >
        {open ? (
          <TariffForm
            api={api}
            title={title}
            from={from ?? null}
            {...(companyId ? { companyId } : {})}
            onSaved={() => {
              onOpenChange(false);
              onSaved();
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TariffForm({
  api,
  title,
  from,
  companyId,
  onSaved,
}: {
  api: Api;
  title: string;
  from: TariffRates | null;
  companyId?: string;
  onSaved: () => void;
}) {
  // The platform list takes effect tomorrow or later (UTC); a company override may start today.
  const today = companyId ? new Date().toISOString().slice(0, 10) : tomorrow();
  const [label, setLabel] = useState("");
  const [date, setDate] = useState(tomorrow());
  const [input, setInput] = useState(from ? rateToInput(from.input_per_m_micros) : "");
  const [output, setOutput] = useState(from ? rateToInput(from.output_per_m_micros) : "");
  const [image, setImage] = useState(from ? rateToInput(from.image_micros) : "");
  const [markup, setMarkup] = useState(
    from ? bpText(from.reported_cost_markup_bp).replace("%", "") : "0",
  );
  // A new version starts from the known units at launch prices; Edit from the row's own lines.
  const [units, setUnits] = useState<UnitPrice[]>(() =>
    (from?.unit_prices ?? KNOWN_TARIFF_UNITS).map((u) => ({ ...u })),
  );
  const [cats, setCats] = useState<CategoryMarkup[]>(() =>
    (from?.category_markups ?? []).map((c) => ({ ...c })),
  );
  const [plans, setPlans] = useState<PlanPrice[]>(() =>
    (from?.plan_prices ?? []).map((p) => ({ ...p })),
  );
  const [planOpen, setPlanOpen] = useState(false);
  const [unitOpen, setUnitOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const ids = {
    label: useId(),
    date: useId(),
    input: useId(),
    output: useId(),
    image: useId(),
    markup: useId(),
  };
  const rates = {
    input: parseRate(input),
    output: parseRate(output),
    image: parseRate(image),
    markup: parsePercent(markup),
  };
  const valid =
    label.trim() !== "" &&
    date >= today &&
    rates.input !== null &&
    rates.output !== null &&
    rates.image !== null &&
    rates.markup !== null &&
    rates.markup <= 100_000 &&
    !unitOpen &&
    !catOpen &&
    !planOpen;
  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const body = {
        label: label.trim(),
        effective_from: date,
        input_per_m_micros: rates.input ?? 0,
        output_per_m_micros: rates.output ?? 0,
        image_micros: rates.image ?? 0,
        unit_prices: units,
        reported_cost_markup_bp: rates.markup ?? 0,
        category_markups: cats,
        // Per-plan prices live on the platform list only (a company's version is its own).
        ...(companyId ? {} : { plan_prices: plans }),
      };
      if (companyId)
        await api.client.operator.wallet.createTariff({
          params: { company_id: companyId },
          idempotencyKey: newIdempotencyKey(),
          body,
        });
      else await api.client.operator.tariffs.create({ idempotencyKey: newIdempotencyKey(), body });
    },
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) save.mutate();
  };
  const bad = (v: string, parsed: number | null) => v !== "" && parsed === null;
  return (
    <form onSubmit={submit} className="grid gap-4">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <FieldGroup>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel htmlFor={ids.label}>Label</FieldLabel>
            <Input
              id={ids.label}
              required
              maxLength={60}
              value={label}
              placeholder="2026-11 list"
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <Field data-invalid={date < today}>
            <FieldLabel htmlFor={ids.date}>Effective from</FieldLabel>
            <Input
              id={ids.date}
              type="date"
              required
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field data-invalid={bad(input, rates.input)}>
            <FieldLabel htmlFor={ids.input}>Input $ / 1M tokens</FieldLabel>
            <Input
              id={ids.input}
              inputMode="decimal"
              required
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </Field>
          <Field data-invalid={bad(output, rates.output)}>
            <FieldLabel htmlFor={ids.output}>Output $ / 1M tokens</FieldLabel>
            <Input
              id={ids.output}
              inputMode="decimal"
              required
              value={output}
              onChange={(e) => setOutput(e.target.value)}
            />
          </Field>
          <Field data-invalid={bad(image, rates.image)}>
            <FieldLabel htmlFor={ids.image}>$ per image</FieldLabel>
            <Input
              id={ids.image}
              inputMode="decimal"
              required
              value={image}
              onChange={(e) => setImage(e.target.value)}
            />
          </Field>
          <Field data-invalid={bad(markup, rates.markup)}>
            <FieldLabel htmlFor={ids.markup} className="inline-flex items-center gap-1">
              Default markup %
              <InfoTip label="About markup">
                Added to costs an agent reports itself, for categories without a markup below.
              </InfoTip>
            </FieldLabel>
            <Input
              id={ids.markup}
              inputMode="decimal"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
            />
          </Field>
        </div>
        <UnitPricesEditor value={units} onChange={setUnits} onEditing={setUnitOpen} />
        <CategoryMarkupsEditor value={cats} onChange={setCats} onEditing={setCatOpen} />
        {companyId ? null : (
          <PlanPricesEditor value={plans} onChange={setPlans} onEditing={setPlanOpen} />
        )}
        {unitOpen || catOpen || planOpen ? (
          <p className="text-xs text-muted-foreground">Save or cancel the open row first.</p>
        ) : null}
        {save.isError ? (
          <FieldError>{problemText(save.error, "The version was not saved.")}</FieldError>
        ) : null}
      </FieldGroup>
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
        <Button type="submit" disabled={!valid || save.isPending}>
          {save.isPending ? "Saving…" : "Save version"}
        </Button>
      </DialogFooter>
    </form>
  );
}
